/**
 * Data hooks for the Teams module (`docs/API.md`'s `/v1/teams` block).
 */
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import * as teamsApi from '../../api/teams.api.js';
import * as usersApi from '../../api/users.api.js';
import { queryKeys } from '../../app/queryClient.js';
import { pushToast } from '../ui/uiSlice.js';

/** @param {object} [params] e.g. `{ q, status, manager_id }` — `manager_id: 'me'` scopes to the caller's own teams. */
export function useTeams(params = {}) {
  return useQuery({
    queryKey: queryKeys.teams.list(params),
    queryFn: () => teamsApi.listTeams(params),
    placeholderData: (previous) => previous,
  });
}

export function useTeam(id, enabled = true) {
  return useQuery({
    queryKey: queryKeys.teams.detail(id),
    queryFn: () => teamsApi.getTeam(id),
    enabled: Boolean(id) && enabled,
  });
}

export function useTeamMembers(id, params = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.teams.members(id, params),
    queryFn: () => teamsApi.listTeamMembers(id, params),
    enabled: Boolean(id) && enabled,
  });
}

/** The org's users, for a "pick who's on this team" list. Every active user — not narrowed to the caller's own team, since the whole point is picking people not on it yet. */
export function useUserPickerOptions() {
  const users = useQuery({
    queryKey: queryKeys.users.list({ limit: 200, picker: true }),
    queryFn: () => usersApi.listUsers({ limit: 200 }),
    staleTime: 300_000,
  });
  const userRows = users.data?.data ?? [];

  return useMemo(() => ({
    users: userRows.map((u) => ({ value: u.id, label: u.full_name ?? u.email, email: u.email })),
    loading: users.isLoading,
  }), [userRows, users.isLoading]);
}

export function useCreateTeam() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: (payload) => teamsApi.createTeam(payload),
    onSuccess: (team) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      dispatch(pushToast({ tone: 'success', message: `Team "${team.name}" created.` }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not create that team', detail: error.message }));
    },
  });
}

export function useUpdateTeam() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, patch, rowVersion }) => teamsApi.updateTeam(id, patch, rowVersion),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      dispatch(pushToast({ tone: 'success', message: 'Team saved.' }));
    },
    onError: (error) => {
      const stale = error?.code === 'stale_write';
      dispatch(pushToast({
        tone: 'danger',
        message: stale ? 'Somebody else changed this' : 'Could not save that change',
        detail: stale ? 'Refresh to pick up their version, then try again.' : error.message,
      }));
    },
  });
}

export function useDeleteTeam() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, reason, rowVersion }) => teamsApi.deleteTeam(id, { reason, rowVersion }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      dispatch(pushToast({ tone: 'success', message: 'Team deleted.' }));
    },
    onError: (error) => {
      const denied = error?.status === 403;
      dispatch(pushToast({
        tone: 'danger',
        message: denied ? "You don't have permission to delete teams" : 'Could not delete that team',
        detail: denied ? 'Ask an administrator.' : error.message,
      }));
    },
  });
}

/**
 * Add users to a team's roster. Success is reported even when some ids were
 * skipped (already a current member, or not a valid user in this org) —
 * those are the API's normal, documented outcomes, not partial failures.
 */
export function useAddTeamMembers() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ id, userIds }) => teamsApi.addTeamMembers(id, userIds),
    onSuccess: (result) => {
      /* `teams.all` is `['teams']` — React Query's default fuzzy matching
         invalidates every key starting with it, members lists included. */
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
      const skipped = result.skipped_already_member + result.skipped_invalid;
      dispatch(pushToast({
        tone: 'success',
        message: `Added ${result.added}${skipped ? `, skipped ${skipped}` : ''} to the team.`,
      }));
    },
    onError: (error) => {
      dispatch(pushToast({ tone: 'danger', message: 'Could not add to that team', detail: error.message }));
    },
  });
}

export function useRemoveTeamMember() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: ({ teamId, userId }) => teamsApi.removeTeamMember(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.teams.all });
    },
    onError: (error) => {
      const conflict = error?.status === 409;
      dispatch(pushToast({
        tone: 'danger',
        message: conflict ? "Can't remove this member" : 'Could not remove that member',
        detail: error.message,
      }));
    },
  });
}
